import { useEffect, useState } from "react";
import { initTelegram, isTelegramEnv } from "./telegram.ts";
import { createSession, ApiError, type PublicUser } from "./api.ts";
import { AgeGate } from "./AgeGate.tsx";
import { Shop } from "./Shop.tsx";

type SessionState =
  | { phase: "loading" }
  | { phase: "no-telegram" }
  | { phase: "error"; message: string }
  | { phase: "ready"; user: PublicUser };

/**
 * App root. Boots the Telegram runtime, establishes a backend session by
 * exchanging initData for a JWT (the trust boundary), then routes the user:
 * unverified -> age gate; verified -> storefront.
 */
export function App() {
  const [state, setState] = useState<SessionState>({ phase: "loading" });

  useEffect(() => {
    initTelegram();

    if (!isTelegramEnv()) {
      setState({ phase: "no-telegram" });
      return;
    }

    createSession()
      .then((user) => setState({ phase: "ready", user }))
      .catch((err) => {
        const message =
          err instanceof ApiError ? err.message : "failed to start session";
        setState({ phase: "error", message });
      });
  }, []);

  const onVerified = (user: PublicUser) => setState({ phase: "ready", user });

  // Opened outside Telegram (a plain browser): the storefront can't run here,
  // so show a dedicated landing page instead of the shop shell.
  if (state.phase === "no-telegram") {
    return <TelegramOnlyLanding />;
  }

  return (
    <main className="screen">
      <header className="header">
        <div className="brand">
          <span className="brand-logo" aria-label="NApuff">
            <svg viewBox="0 0 44 44" width="30" height="30" fill="none" role="img">
              <path
                d="M12 32 V13 L32 32 V13"
                stroke="#ffffff"
                strokeWidth="4.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="34.5" cy="11.5" r="3.6" fill="#ffffff" />
            </svg>
          </span>
          <div>
            <h1 className="brand-name">NApuff</h1>
            <p className="brand-tag">Groningen · fast local delivery 🛵</p>
          </div>
        </div>
      </header>

      <SessionBody state={state} onVerified={onVerified} />
    </main>
  );
}

/**
 * Standalone landing shown when the app is opened in a normal browser (no
 * Telegram runtime). NApuff only operates inside Telegram, so this page's one
 * job is to explain that and hand the visitor a single button into the bot.
 */
function TelegramOnlyLanding() {
  return (
    <main className="tg-landing">
      <div className="tg-landing-inner">
        <span className="tg-landing-logo" aria-label="NApuff">
          <svg viewBox="0 0 44 44" width="40" height="40" fill="none" role="img">
            <path
              d="M12 32 V13 L32 32 V13"
              stroke="#ffffff"
              strokeWidth="4.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="34.5" cy="11.5" r="3.6" fill="#ffffff" />
          </svg>
        </span>

        <h1 className="tg-landing-title">NApuff lives on Telegram</h1>
        <p className="tg-landing-sub">
          Our vape shop in Groningen runs entirely inside Telegram — that’s where
          you browse, order, and get it delivered to your door. 🛵
        </p>

        <a
          className="tg-landing-cta"
          href="https://t.me/vapebrogroningen_bot?start=shop"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
            <path d="M9.8 16.6 9.6 20c.4 0 .6-.2.8-.4l1.9-1.8 3.9 2.9c.7.4 1.2.2 1.4-.7l2.6-12.1c.3-1.1-.4-1.6-1.1-1.3L3.3 10.3c-1.1.4-1.1 1-.2 1.3l3.9 1.2 9-5.7c.4-.3.8-.1.5.2l-6.7 9.3Z" />
          </svg>
          Open in Telegram
        </a>
      </div>
    </main>
  );
}

function SessionBody({
  state,
  onVerified,
}: {
  // `no-telegram` is handled by the App root before SessionBody renders.
  state: Exclude<SessionState, { phase: "no-telegram" }>;
  onVerified: (user: PublicUser) => void;
}) {
  if (state.phase === "loading") {
    return (
      <section className="card">
        <div className="row">
          <span className="row-label">Session</span>
          <span className="row-value">connecting…</span>
        </div>
      </section>
    );
  }

  if (state.phase === "error") {
    return (
      <p className="note error">Couldn’t start a session: {state.message}</p>
    );
  }

  const { user } = state;

  if (user.verificationStatus !== "verified") {
    return <AgeGate user={user} onVerified={onVerified} />;
  }

  // Verified → the storefront.
  return <Shop user={user} onUserUpdate={onVerified} />;
}
